import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import MovieIcon from '@mui/icons-material/Movie';
import { useAppContext } from '../context/AppContext';
import VideoUploader, { UploadedVideo } from '../components/VideoUploader';
import AudioUploader, { UploadedAudio } from '../components/AudioUploader';
import VideoPlayer from '../components/VideoPlayer';
import GenerationHistory from '../components/GenerationHistory';

import { videoLipsyncApi } from '../services/api';

const processVideoDataUri = (data_uri: string): string => {
  if (data_uri.startsWith('http://') || data_uri.startsWith('https://')) {
    return data_uri;
  }

  const clean = data_uri.startsWith('data:') ? data_uri.split(',')[1] : data_uri;
  return `data:video/mp4;base64,${clean}`;
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));
const PromptTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: theme.palette.divider },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
  },
}));

const LIPSYNC_QUEUES = ['wan21-v2v-lipsync-pro'];

const RESOLUTIONS = [
  { label: '480p', value: 480 },
  { label: '720p', value: 720 },
];

const DURATIONS = [
  { label: '5 sec', value: 5 },
  { label: '8 sec', value: 8 },
  { label: '10 sec', value: 10 },
];

const VideoLipsyncGenerator: React.FC = () => {
  const { state, actions } = useAppContext();
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartTimeRef = useRef<number | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [selectedQueue, setSelectedQueue] = useState<string>(LIPSYNC_QUEUES[0]);
  const [resolution, setResolution] = useState<number>(480);
  const [duration, setDuration] = useState<number>(5);
  const [uploadedVideo, setUploadedVideo] = useState<UploadedVideo | null>(null);
  const [uploadedAudio, setUploadedAudio] = useState<UploadedAudio | null>(null);

  const { errorMessage, generationProgress } = state;
  const { prompt, isGenerating, generatedVideo } = state;

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  const uploadToR2 = async (file: File, prefix: string): Promise<string> => {
    const form = new FormData();
    form.append('file', file);
    form.append('prefix', prefix);
    const res = await fetch('/api/uploads', { method: 'POST', body: form });
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
    const data: any = await res.json();
    const url = String(data.url || '');
    return new URL(url, window.location.origin).toString();
  };

  const handleVideoUpload = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const blobUrl = URL.createObjectURL(file);
    setUploadedVideo({ file, preview: blobUrl });
  };

  const handleAudioUpload = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const blobUrl = URL.createObjectURL(file);
    setUploadedAudio({ file, preview: blobUrl });
  };

  const handleRemoveVideo = () => setUploadedVideo(null);
  const handleRemoveAudio = () => setUploadedAudio(null);
  const setErrorMessage = (message: string) => actions.setError(message);
  const handlePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => actions.setPrompt(e.target.value);

  const handleReset = () => {
    actions.prepareNewGenerationNoHistory();
    setErrorMessage('');
    setStatusMessage('');
  };

  const clearPolling = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const startProgressPolling = (taskId: string) => {
    setStatusMessage('Starting... grab a coffee, it usually takes ~3 minutes');
    const pollInterval = 10000;
    const maxPollTime = 600000;
    actions.updateGenerationProgress(0);
    pollStartTimeRef.current = Date.now();
    clearPolling();

    let currentIntervalId: any = null;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;

    const pollForStatus = async () => {
      const currentTime = Date.now();
      const startTime = pollStartTimeRef.current ?? currentTime;
      const elapsedTime = currentTime - startTime;
      if (elapsedTime > maxPollTime) {
        if (currentIntervalId) {
          clearInterval(currentIntervalId);
          progressIntervalRef.current = null;
        }
        setErrorMessage('Task timeout - generation took too long');
        actions.prepareNewGenerationNoHistory();
        return;
      }
      try {
        const response = await videoLipsyncApi.checkStatus(taskId, selectedQueue);
        const { status, progress: progressVal, statusMessages, queue_position } = response as any;
        const progress = typeof progressVal === 'number' ? progressVal : undefined;
        consecutiveErrors = 0;
        if (typeof progress === 'number') actions.updateGenerationProgress(progress * 100);
        if (statusMessages) setStatusMessage(statusMessages.detail_message || statusMessages.message || '');
        else if (typeof progress === 'number') setStatusMessage(`Processing: ${Math.round(progress * 100)}%`);

        if (status && status.toLowerCase() === 'in_queue') {
          const tagline = queue_position ? `${queue_position} ahead of you.` : "You're next.";
          setStatusMessage(`One moment. We'll start generating shortly. ${tagline}`);
          return;
        }

        if (status && status.toLowerCase() === 'cancelled') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setStatusMessage('Video generation cancelled');
          setErrorMessage('Task was cancelled');
          actions.prepareNewGenerationNoHistory();
          return;
        }

        if (status && status.toLowerCase() === 'failed') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          const failureReason = statusMessages?.message || statusMessages?.detail_message || 'Unknown error';
          setStatusMessage(`Video generation failed: ${failureReason}`);
          setErrorMessage(
            `Task failed: ${failureReason}. This may be due to file format, prompt complexity, or processing constraints. Please try with different inputs.`,
          );
          actions.prepareNewGenerationNoHistory();
          return;
        }

        if (status && status.toLowerCase() === 'completed') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setStatusMessage('Video generation completed! Fetching result ...');

          const { video } = await videoLipsyncApi.getResult(taskId, selectedQueue);
          const data_uri = video?.data_uri;

          if (video && data_uri) {
            const dataUriStr = data_uri as string;
            if (typeof dataUriStr !== 'string' || dataUriStr.length === 0) {
              setErrorMessage('Invalid video data received');
              actions.prepareNewGeneration();
              return;
            }

            setStatusMessage('Video generation completed.');
            const videoUrl = processVideoDataUri(dataUriStr);
            actions.completeGeneration(videoUrl);
          } else {
            setErrorMessage('Video generated but no data was provided');
            actions.prepareNewGeneration();
          }
        }
      } catch (err: any) {
        if (err.response && (err.response.status === 404 || err.response.status === 422)) {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setErrorMessage(`Task error: ${err.response?.data?.detail || 'Unknown error'}`);
          actions.prepareNewGenerationNoHistory();
        } else {
          consecutiveErrors++;
          if (consecutiveErrors >= maxConsecutiveErrors) {
            if (currentIntervalId) {
              clearInterval(currentIntervalId);
              progressIntervalRef.current = null;
            }
            setErrorMessage(`Too many polling errors: ${err.message || 'Unknown error'}`);
            actions.prepareNewGenerationNoHistory();
          } else {
            setErrorMessage(
              `Polling error: ${err.message || 'Unknown error'} - Will retry... (${consecutiveErrors}/${maxConsecutiveErrors})`,
            );
          }
        }
      }
    };

    pollForStatus();
    const interval = setInterval(pollForStatus, pollInterval);
    currentIntervalId = interval;
    progressIntervalRef.current = interval;
    return interval;
  };

  const handleGenerate = async () => {
    if (!uploadedVideo || !uploadedAudio) {
      alert('Please upload a video and an audio file. Prompt is optional.');
      return;
    }
    setErrorMessage('');
    setStatusMessage('Uploading files...');

    try {
      let videoUrl: string;
      let audioUrl: string;

      try {
        setStatusMessage('Uploading video to R2...');
        videoUrl = await uploadToR2(uploadedVideo.file, 'videos');
      } catch (uploadError) {
        const reader = new FileReader();
        const videoBase64 = await new Promise<string>((resolve) => {
          reader.onload = () => resolve(String(reader.result));
          reader.readAsDataURL(uploadedVideo.file);
        });
        videoUrl = videoBase64;
      }

      try {
        setStatusMessage('Uploading audio to R2...');
        audioUrl = await uploadToR2(uploadedAudio.file, 'audio');
      } catch (uploadError) {
        const reader = new FileReader();
        const audioBase64 = await new Promise<string>((resolve) => {
          reader.onload = () => resolve(String(reader.result));
          reader.readAsDataURL(uploadedAudio.file);
        });
        audioUrl = audioBase64;
      }

      setStatusMessage('Submitting request...');

      const payload: Record<string, unknown> = {
        prompt: prompt || '',
        queue: selectedQueue,
        min_height: resolution,
        duration,
      };

      const videoIsUrl = videoUrl.startsWith('http://') || videoUrl.startsWith('https://');
      if (videoIsUrl) {
        (payload as any).image_url = videoUrl;
      } else {
        const dataUrl = videoUrl.startsWith('data:') ? videoUrl : `data:video/*;base64,${videoUrl}`;
        (payload as any).image_uri = dataUrl.split(',')[1] || '';
      }

      const audioIsUrl = audioUrl.startsWith('http://') || audioUrl.startsWith('https://');
      if (audioIsUrl) {
        (payload as any).audio_url = audioUrl;
      } else {
        (payload as any).audio_uri = audioUrl.startsWith('data:') ? (audioUrl.split(',')[1] || '') : audioUrl;
      }

      const { request_id } = await videoLipsyncApi.generateVideo(payload);
      actions.startGeneration(request_id);
      startProgressPolling(request_id);
    } catch (error: any) {
      let errorDetail = 'Unknown error';
      if (error.response?.data?.detail) errorDetail = error.response.data.detail;
      else if (error.message) errorDetail = error.message;
      setErrorMessage(`Failed to start video generation: ${errorDetail}`);
      alert(`Error: ${errorDetail}`);
      actions.cancelGeneration();
    }
  };

  return (
    <Grid container spacing={3} sx={{ height: 'calc(100vh - 120px)' }}>
      <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <StyledPaper elevation={3} sx={{ mb: 3, flex: '1 1 auto' }}>
          <Typography variant="h5" gutterBottom>
            Video & Audio Upload
          </Typography>
          <Box sx={{ mb: 2 }}>
            <VideoUploader uploadedVideo={uploadedVideo} onUpload={handleVideoUpload} onRemove={handleRemoveVideo} />
          </Box>
          <Box sx={{ mb: 3 }}>
            <AudioUploader uploadedAudio={uploadedAudio} onUpload={handleAudioUpload} onRemove={handleRemoveAudio} />
          </Box>

          <Typography variant="h5" gutterBottom>
            Options
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel id="lipsync-queue-label">Queue</InputLabel>
              <Select
                labelId="lipsync-queue-label"
                id="lipsync-queue"
                value={selectedQueue}
                label="Queue"
                disabled={isGenerating}
                onChange={(e) => setSelectedQueue(e.target.value as string)}
              >
                {LIPSYNC_QUEUES.map((q) => (
                  <MenuItem key={q} value={q}>
                    {q}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: 180 }}>
              <InputLabel id="lipsync-resolution-label">Resolution</InputLabel>
              <Select
                labelId="lipsync-resolution-label"
                id="lipsync-resolution"
                value={resolution}
                label="Resolution"
                disabled={isGenerating}
                onChange={(e) => setResolution(Number(e.target.value))}
              >
                {RESOLUTIONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: 180 }}>
              <InputLabel id="lipsync-duration-label">Duration</InputLabel>
              <Select
                labelId="lipsync-duration-label"
                id="lipsync-duration"
                value={duration}
                label="Duration"
                disabled={isGenerating}
                onChange={(e) => setDuration(Number(e.target.value))}
              >
                {DURATIONS.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Typography variant="h5" gutterBottom>
            Prompt (optional)
          </Typography>
          <PromptTextField
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            variant="outlined"
            placeholder="Enter an optional prompt here..."
            value={prompt}
            onChange={handlePromptChange}
            sx={{ mb: 3, maxHeight: 160, overflow: 'auto' }}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 'auto' }}>
            {!generatedVideo ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={!uploadedVideo || !uploadedAudio || isGenerating}
                  onClick={handleGenerate}
                  startIcon={<MovieIcon />}
                  fullWidth
                >
                  Generate Video
                </Button>
                {isGenerating && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="large"
                    onClick={() => {
                      if (state.generationTaskId) {
                        clearPolling();
                        videoLipsyncApi
                          .cancelTask(state.generationTaskId, selectedQueue)
                          .then(() => {
                            actions.prepareNewGenerationNoHistory();
                            setStatusMessage('Video generation cancelled');
                          })
                          .catch((err: any) => {
                            actions.prepareNewGenerationNoHistory();
                            setErrorMessage('Error cancelling task: ' + (err.message || 'Unknown error'));
                          });
                      }
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => {
                  clearPolling();
                  actions.prepareNewGenerationNoHistory();
                  handleGenerate();
                }}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                New Generation
              </Button>
            )}
          </Box>
        </StyledPaper>
        <Box sx={{ flex: '0 0 auto', height: '250px' }}>
          <GenerationHistory />
        </Box>
      </Grid>
      <Grid item xs={12} md={6} sx={{ height: '100%' }}>
        <StyledPaper elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h5" gutterBottom>
            Generating - Usually takes 1-2 minutes
          </Typography>
          {isGenerating ? (
            <Box sx={{ width: '100%', mt: 4, position: 'relative' }}>
              <Typography variant="body1" gutterBottom>
                {statusMessage || 'Generating video... This may take 1-2 minutes.'}
              </Typography>
              {errorMessage ? (
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    borderLeft: '4px solid',
                    borderColor: 'error.main',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Error Occurred
                  </Typography>
                  <Typography variant="body1">{errorMessage}</Typography>
                  <Button variant="contained" color="error" size="small" sx={{ mt: 1 }} onClick={handleReset}>
                    Reset and Try Again
                  </Button>
                </Paper>
              ) : (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={generationProgress}
                    sx={{ height: 10, borderRadius: 5, mb: 1, '& .MuiLinearProgress-bar': { animation: 'pulse 1.5s infinite ease-in-out' } }}
                  />
                  <Typography variant="body2" color="text.secondary" align="right">{`${Math.round(
                    generationProgress,
                  )}%`}</Typography>
                </>
              )}
              <Box
                sx={{
                  mt: 3,
                  width: '100%',
                  height: '300px',
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  position: 'relative',
                }}
              >
                <Typography variant="h6" color="white" align="center">
                  {statusMessage || 'Processing your video...'}
                </Typography>
              </Box>
            </Box>
          ) : generatedVideo ? (
            <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: '0 0 auto' }}>
                <VideoPlayer videoUrl={generatedVideo} />
              </Box>
            </Box>
          ) : (
            <Box sx={{ width: '100%', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
              <Typography variant="body1">No video generated yet. Start by uploading a video and an audio file.</Typography>
            </Box>
          )}
        </StyledPaper>
      </Grid>
    </Grid>
  );
};

export default VideoLipsyncGenerator;
